import * as _ from 'lodash-es';
import moment from 'moment';
import type { AttributeMap, Op } from 'quill/core';
import { Delta } from 'quill/core';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import {
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import Page from '../../app/layout/Page';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import Timestamp from '../../components/Timestamp';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import Gutter from '../../editor/Gutter';
import GutterConfigProvider from '../../editor/GutterConfigProvider';
import QuillContext, { useQuill } from '../../editor/QuillContext';
import UIElementResizeContext from '../../editor/UIElementResizeContext';
import type { MagneticWallHighlight } from '../../editor/editorSlice';
import useAnchorScrollingHandler from '../../editor/useAnchorScrollingHandler';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import copyIcon from '../../icons/non-prefilled/16x16/copy.svg';
import editIcon from '../../icons/non-prefilled/16x16/edit.svg';
import retryIcon from '../../icons/non-prefilled/16x16/retry.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { miscToasts, postToasts } from '../../toasts/toasts';
import InternalTopbar from '../../topbar/InternalTopbar';
import { UIActions } from '../../ui/uiSlice';
import Logger from '../../utils/Logger';
import PageMeta from '../../utils/PageMeta';
import ignoreInChromatic from '../../utils/ignoreInChromatic';
import { isCommentOrPublicCommentThread } from '../../utils/post/threadUtils';
import useNavigate from '../../utils/router/useNavigate';
import useSlug from '../../utils/router/useSlug';
import { getThreadIdFromUrlHash } from '../../utils/url/commentThreadHash';
import postPath from '../../utils/url/postPath';
import PostLayout from '../PostLayout';
import PostLayoutProvider from '../PostLayoutProvider';
import { PostHistoryViewTOC } from '../PostStickyControl';
import ControlBarContributorsButton from '../controlBar/ControlBarContributorsButton';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import useCanDisplayPostHistoryView from '../useCanDisplayPostHistoryView';
import useDuplicatePost from '../useDuplicatePost';
import useLazyHistoricalCommentThreadPath from '../useLazyHistoricalCommentThreadPath';
import useVisibleMagneticWallItems from '../useVisibleMagneticWallItems';
import collectThreadAndAnnotationIds from '../utils/collectThreadAndAnnotationIds';
import isPostContentEmpty from '../utils/isPostContentEmpty';
import type { AnnotationVersionChange } from './AnnotationVersionChangeCard';
import { toAnnotationVersionChange } from './AnnotationVersionChangeCard';
import EditFacesMagneticWall from './EditFacesMagneticWall';
import PostHistoryMagneticWall from './PostHistoryMagneticWall';
import * as styles from './PostHistoryView.module.scss';
import type { ThreadVersionChange } from './ThreadVersionChangeCard';
import { toThreadVersionChange } from './ThreadVersionChangeCard';
import VersionChanges, {
  useVersionChangeMagneticWallFocus,
} from './VersionChanges';
import VersionChangesDropdown from './VersionChangesDropdown';
import VersionNameForm from './VersionNameForm';
import type { EditVersionChange } from './calculateVersionChanges';
import calculateVersionChanges from './calculateVersionChanges';
import partitionHistories from './partitionHistories';
import useDuplicatePostFromHistory from './useDuplicatePostFromHistory';
import useRestoreVersion from './useRestoreVersion';

const ANNOTATION_FRAGMENT = gql(`
  fragment BasePostHistoryView_annotation on Annotation {
    id
    insertedAt
    ...PostHistoryMagneticWall_annotation
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment BasePostHistoryView_thread on CommentThread {
    id
    ...PostHistoryMagneticWall_thread
  }
`);

const POST_FRAGMENT = gql(`
  fragment BasePostHistoryView_post on Post {
    id
    title
    publishedAt
    archivedAt
    ...ControlBarContributorsButton_post
    ...usePostPolicyCheck_post
  }
`);

// Backwards compatibility for /posts/:postId/histories/:versionFrom/:versionTo
export const PostHistoryRedirect = () => {
  const [id] = useSlug('postSlug');
  const params = useParams() as {
    versionFrom?: string;
    versionTo?: string;
  };

  const histories =
    params.versionFrom && params.versionTo
      ? (`${parseInt(params.versionFrom, 10)}-${parseInt(params.versionTo, 10)}` as const)
      : '';

  return <Navigate to={postPath({ id, title: '' }, { histories })} replace />;
};

// Support for /posts/:postId/comments#threadId
export const HistoricalThreadRedirect = () => {
  const [postId] = useSlug('postSlug');
  const getHistoricalCommentThreadPath = useLazyHistoricalCommentThreadPath();
  const canDisplayPostHistoryView = useCanDisplayPostHistoryView();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    const threadId = getThreadIdFromUrlHash(location.hash);

    const redirectToPost = () => {
      navigate(postPath({ id: postId, title: '' }), { replace: true });
    };

    // This handles 2 cases:
    //
    // 1. no threadId: /posts/:postId/comments (without threadId as a hash)
    // was a valid url in the old history view so we handle it to be sure
    //
    // 2. canDisplayPostHistoryView is false: avoid endless loop if
    // we can't display the post history view
    if (!threadId || !canDisplayPostHistoryView) {
      redirectToPost();
      return undefined;
    }

    let isMounted = true;

    getHistoricalCommentThreadPath(postId, threadId)
      .then((redirectPath) => {
        if (redirectPath) {
          if (isMounted) {
            navigate(redirectPath, { replace: true });
          }
        } else {
          dispatch(UIActions.pushToast(postToasts.threadNotFoundError));
          redirectToPost();
        }
      })
      .catch(() => {
        dispatch(UIActions.pushToast(miscToasts.genericError));
        redirectToPost();
      });

    return () => {
      isMounted = false;
    };
  }, [
    location.hash,
    postId,
    getHistoricalCommentThreadPath,
    navigate,
    canDisplayPostHistoryView,
    dispatch,
  ]);

  return null;
};

type VersionRange = {
  from: number;
  to: number;
};

export type BasePostHistoryViewMagneticWallVersionChanges =
  | ThreadVersionChange<DocumentType<typeof THREAD_FRAGMENT>>
  | AnnotationVersionChange<DocumentType<typeof ANNOTATION_FRAGMENT>>;

export const useMagneticWallVersionChanges = ({
  threads,
  annotations,
  compareDate,
  content,
}: {
  threads?: DocumentType<typeof THREAD_FRAGMENT>[];
  annotations?: DocumentType<typeof ANNOTATION_FRAGMENT>[];
  compareDate?: moment.Moment;
  content: Delta | undefined;
}): BasePostHistoryViewMagneticWallVersionChanges[] => {
  return useMemo(() => {
    const { allThreadIds, allAnnotationIds } = collectThreadAndAnnotationIds(
      content?.ops ?? [],
    );

    return [
      ...(threads?.map((thread) =>
        toThreadVersionChange(thread, {
          newCommentIds: compareDate
            ? new Set(
                thread.comments
                  .filter((comment) =>
                    moment(comment.insertedAt).isAfter(compareDate),
                  )
                  .map((comment) => comment.id),
              )
            : new Set(),
          // Threads without context can be shown in the magnetic wall
          // but will look differently, so we mark them as such
          isMissingContext: !allThreadIds.has(thread.id),
        }),
      ) ?? []),
      ...(annotations
        // Annotations are considered part of the content, so we will never
        // show them if the context was deleted. So we filter the annotations
        // we get from the server by the ones that are actually in the content
        ?.filter((annotation) => allAnnotationIds.has(annotation.id))
        ?.map((annotation) =>
          toAnnotationVersionChange(annotation, {
            isNew: compareDate
              ? moment(annotation.insertedAt).isAfter(compareDate)
              : false,
          }),
        ) ?? []),
    ];
  }, [threads, annotations, compareDate, content]);
};

interface VisibleMagneticWallVersionChangesProps {
  magneticWallVersionChanges: BasePostHistoryViewMagneticWallVersionChanges[];
  magneticWallHighlight: MagneticWallHighlight | null;
}

const VisibleMagneticWallVersionChanges = ({
  magneticWallVersionChanges,
  magneticWallHighlight,
}: VisibleMagneticWallVersionChangesProps) => {
  const { magneticWallChangeFocus } = useVersionChangeMagneticWallFocus();

  useVisibleMagneticWallItems(
    magneticWallVersionChanges.filter((item) => {
      if (item.type === 'comment') {
        return (
          item.thread.resolvedAt == null ||
          // If the thread is resolved, we only show focus & highlight
          magneticWallChangeFocus?.id === item.thread.id ||
          magneticWallHighlight?.id === item.thread.id
        );
      }
      return true;
    }),
  );

  return null;
};

interface BasePostHistoryViewProps {
  editVersionChanges?: EditVersionChange[];
  editedAt: string;
  versionRange?: VersionRange;
  content?: Delta;
  magneticWallVersionChanges: BasePostHistoryViewMagneticWallVersionChanges[];
  post: DocumentType<typeof POST_FRAGMENT>;
  topbar: React.ReactNode;
}

export const BasePostHistoryView = ({
  editVersionChanges,
  versionRange,
  editedAt,
  content,
  magneticWallVersionChanges,
  post,
  topbar,
}: BasePostHistoryViewProps) => {
  const { quill } = useQuill();

  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();
  const canEdit = postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);

  const [hoverableAreaContainer, setHoverableAreaContainer] =
    useState<HTMLElement | null>(null);
  const [magneticWallHighlight, setMagneticWallHighlight] =
    useState<MagneticWallHighlight | null>(null);

  const anchorScrollingHandlerProps = useAnchorScrollingHandler(
    quill,
    post.id,
    {
      updateLocationHash: true,
    },
  );

  const hasHeadings = useMemo(
    () => content?.ops.some((op) => op.attributes?.header != null),
    [content?.ops],
  );

  const allVersionChanges = useMemo(
    () => [
      ...(editVersionChanges ?? []),
      ...magneticWallVersionChanges.filter((v) => {
        if (v.type === 'comment') {
          return v.newCommentIds.size > 0;
        }

        if (v.type === 'annotation') {
          return v.isNew;
        }

        return true;
      }),
    ],
    [editVersionChanges, magneticWallVersionChanges],
  );

  return (
    <VersionChanges.Root allVersionChanges={allVersionChanges}>
      <VisibleMagneticWallVersionChanges
        magneticWallVersionChanges={magneticWallVersionChanges}
        magneticWallHighlight={magneticWallHighlight}
      />
      <PageMeta title={`${post.title} - History - Slab`} />
      <PostLayoutProvider
        initialPriorityList={['sidebar', 'magneticWall', 'toc']}
        // Force new layout when the version changes
        key={versionRange && `${versionRange.from}-${versionRange.to}`}
        expandableAreas={{
          sidebar: true,
          toc: hasHeadings,
          magneticWall: magneticWallVersionChanges.length > 0,
        }}
      >
        <PostLayout.Root
          {...anchorScrollingHandlerProps}
          topbar={topbar}
          forceExpandOnMonitor
        >
          <UIElementResizeContext>
            <PostLayout.ControlBar>
              <div className={styles.controlBar}>
                <ControlBarContributorsButton post={post} interactionsDisabled>
                  Edited <Timestamp date={editedAt} {...ignoreInChromatic()} />
                </ControlBarContributorsButton>
              </div>
            </PostLayout.ControlBar>
            <PostLayout.Above />
            <PostLayout.TOC>
              <PostHistoryViewTOC postId={post.id} />
            </PostLayout.TOC>
            <PostLayout.Editor ref={setHoverableAreaContainer}>
              <GutterConfigProvider
                config={{
                  listCollapsingEnabled: true,
                }}
              >
                <VersionChanges.Post content={content} />
                {quill && hoverableAreaContainer && (
                  <Gutter
                    quill={quill}
                    hoverableAreaContainer={hoverableAreaContainer}
                  />
                )}
                <PostLayout.Gutter>
                  <EditFacesMagneticWall />
                </PostLayout.Gutter>
              </GutterConfigProvider>
            </PostLayout.Editor>
            <PostLayout.MagneticWall>
              <PostHistoryMagneticWall
                versionChanges={magneticWallVersionChanges}
                canEdit={canEdit}
                magneticWallHighlight={magneticWallHighlight}
                setMagneticWallHighlight={setMagneticWallHighlight}
              />
            </PostLayout.MagneticWall>
            {post.publishedAt && (
              <PostLayout.Below>
                <div className={styles.footerControlBar}>
                  <ControlBarContributorsButton
                    post={post}
                    interactionsDisabled
                  >
                    {post.archivedAt ? (
                      <>
                        Archived <Timestamp date={post.archivedAt} />
                      </>
                    ) : (
                      <>
                        Published <Timestamp date={post.publishedAt} />
                      </>
                    )}
                  </ControlBarContributorsButton>
                </div>
              </PostLayout.Below>
            )}
          </UIElementResizeContext>
        </PostLayout.Root>
      </PostLayoutProvider>
    </VersionChanges.Root>
  );
};

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const DELETE_HISTORY_MUTATION = gql(/** @complexity 2 */ `
  mutation DeleteHistory($id: ID!) {
    deleteHistories(historyId: $id)
  }
`);

const POST_HISTORY_VIEW_OVERFLOW_MENU_ITEMS_POST_FRAGMENT = gql(`
  fragment PostHistoryViewOverflowMenuItems_post on Post {
    id
    histories {
      id
      versionTo
      ...partitionHistories_history
    }
    ...usePostPolicyCheck_post
  }
`);

const PostHistoryViewOverflowMenuItems = ({
  versionRange,
  post,
  onEditVersionName,
  lastHistoryId,
}: {
  versionRange: VersionRange;
  post: DocumentType<
    typeof POST_HISTORY_VIEW_OVERFLOW_MENU_ITEMS_POST_FRAGMENT
  >;
  onEditVersionName: (() => void) | null;
  lastHistoryId: string | null;
}) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const [, setSearchParams] = useSearchParams();

  const postPolicyCheck = usePostPolicyCheck();
  const restoreVersion = useRestoreVersion();
  const duplicateVersion = useDuplicatePostFromHistory();
  const duplicatePost = useDuplicatePost();
  const [deleteHistory, { loading: isDeletingHistory }] = useMutation(
    DELETE_HISTORY_MUTATION,
  );

  const canEdit = postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);

  const { accessibleHistories } = partitionHistories(post.histories);
  const lastAccessibleHistory = accessibleHistories.at(0);

  const isLatestVersion =
    lastAccessibleHistory &&
    versionRange.to === lastAccessibleHistory.versionTo;

  const canRestore = canEdit && lastAccessibleHistory && !isLatestVersion;

  return (
    <>
      <MenuItem
        icon={retryIcon}
        disabled={!canRestore}
        tooltip={(() => {
          if (!canEdit) {
            return 'You do not have\npermission to restore\nthis version';
          }

          return isLatestVersion
            ? 'This is the most\nrecent history'
            : undefined;
        })()}
        tooltipPlacement="top"
        onClick={() => {
          dispatch(
            UIActions.openModal(ConfirmationModal, {
              title: 'Restore version?',
              description:
                'This action will update this post’s contents to this version’s contents. This restore will appear as a new version added to this post’s history.',
              confirmButton: {
                text: 'Restore',
                onClick: () => {
                  return restoreVersion({
                    postId: post.id,
                    versionTo: versionRange.to,
                  });
                },
              },
            }),
          );
        }}
      >
        Restore this version
      </MenuItem>
      <MenuItem
        icon={copyIcon}
        onClick={() => {
          if (isLatestVersion) {
            duplicatePost(post.id);
          } else {
            duplicateVersion({
              postId: post.id,
              versionTo: versionRange.to,
            });
          }
        }}
      >
        Make a copy
      </MenuItem>
      <MenuItem
        disabled={!canEdit || !onEditVersionName}
        icon={editIcon}
        tooltip={(() => {
          if (!canEdit) {
            return 'You do not have\npermission to add or\nrename this version';
          }

          if (!onEditVersionName) {
            return 'Renaming is disabled because\nthe current range spans\nmultiple versions.';
          }

          return undefined;
        })()}
        tooltipPlacement="top"
        onClick={onEditVersionName ?? undefined}
      >
        Rename version
      </MenuItem>
      <MenuSeparator />
      <MenuItem
        danger
        icon={trashIcon}
        tooltip={
          canEdit
            ? undefined
            : 'You do not have\npermission to delete\nthis version'
        }
        tooltipPlacement="top"
        disabled={!canEdit || !lastHistoryId || isDeletingHistory}
        onClick={() => {
          dispatch(
            UIActions.openModal(ConfirmationModal, {
              title: 'Delete history?',
              description:
                'This action will remove this and earlier versions. The post’s history will then appear as if it was created without these changes.',
              confirmButton: {
                text: 'Delete',
                onClick: async () => {
                  if (!lastHistoryId) return;

                  try {
                    await deleteHistory({
                      variables: { id: lastHistoryId },
                    });

                    if (isLatestVersion) {
                      setSearchParams({
                        histories: '',
                      });
                    } else {
                      setSearchParams({
                        histories: `${versionRange.to}-${versionRange.to + 1}`,
                      });
                    }
                  } catch (error) {
                    Logger.error(error);

                    dispatch(UIActions.pushToast(miscToasts.genericError));
                  }
                },
              },
            }),
          );
        }}
      >
        Delete this version
      </MenuItem>
    </>
  );
};

function isOp(opOrObject: unknown): opOrObject is Op {
  return (
    typeof opOrObject === 'object' &&
    opOrObject != null &&
    'attributes' in opOrObject
  );
}

const mapAttributes = <O,>(
  opOrObject: O,
  map: (value: AttributeMap) => void,
): O => {
  if (isOp(opOrObject)) {
    return {
      ...opOrObject,
      attributes: opOrObject.attributes
        ? map(opOrObject.attributes)
        : undefined,
    };
  }

  if (Array.isArray(opOrObject)) {
    return opOrObject.map((v) => mapAttributes(v, map)) as O;
  }

  if (typeof opOrObject === 'object' && opOrObject != null) {
    return Object.fromEntries(
      Object.entries(opOrObject).map(([key, value]) => [
        key,
        mapAttributes(value, map),
      ]),
    ) as O;
  }

  return opOrObject;
};

export const useCalculatedVersionChanges = ({
  diff,
  snapshot,
}: {
  diff: Op[] | undefined;
  snapshot: Op[] | undefined;
}) => {
  // We have this in a separate useMemo to avoid recalculating the version changes
  // when history changes but the diff object is the same. This is for example
  // the case when the user unresolves a thread.
  const calculated = useMemo(() => {
    if (!diff) return null;

    let snapshotDelta = new Delta(snapshot);

    // The snapshot of the first version of the post still has authors for the line breaks.
    // We don't want to show those authors in the full authorship color mode.
    if (isPostContentEmpty(snapshotDelta)) {
      snapshotDelta = new Delta(
        snapshotDelta.map((op) => {
          return { ...op, attributes: _.omit(op.attributes, 'author') };
        }),
      );
    }

    return calculateVersionChanges(snapshotDelta.compose(new Delta(diff)));
  }, [diff, snapshot]);

  const { content, editVersionChanges } = useMemo(() => {
    if (!diff || !calculated) {
      return { content: undefined, allVersionChanges: undefined };
    }

    const composed = new Delta(snapshot)
      .compose(
        new Delta(
          new Delta(diff).map((op) => {
            // recursively map attributes of an op or object
            // because comments could be deeply nested in code-embed or table-embed
            return mapAttributes(op, (attributes) => {
              // We want to display resolved comments, so we don't want to
              // have the diff remove comments from the snapshot
              if (attributes.comment === null) {
                return _.omit(attributes, 'comment');
              }

              return attributes;
            });
          }),
        ),
      )
      .compose(calculated.delta ?? new Delta());

    return {
      content: composed,
      editVersionChanges: calculated.versionChanges,
    };
  }, [calculated, snapshot, diff]);

  return { content, editVersionChanges };
};

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const POST_HISTORY_VIEW_QUERY = gql(/** @complexity 47 */ `
  query PostHistoryView($id: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $id) {
      id
      historyRange(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        insertedAt
        diff
        snapshot
        annotations {
          id
          insertedAt
          ...BasePostHistoryView_annotation
        }
        threads {
          id
          ...BasePostHistoryView_thread
        }
      }
      histories {
        id
        versionFrom
        versionTo
        name
        ...partitionHistories_history
      }
      ...BasePostHistoryView_post
      ...VersionChangesDropdown_post
      ...PostHistoryViewOverflowMenuItems_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

const getSelectedVersionRange = (
  histories: DocumentType<typeof POST_HISTORY_VIEW_QUERY>['post']['histories'],
  versionRange: VersionRange,
): VersionRange | null => {
  const { accessibleHistories } = partitionHistories(histories ?? []);
  const minHistory = accessibleHistories.find(
    (history) => versionRange.from >= history.versionFrom,
  );

  const maxHistory =
    accessibleHistories
      .toReversed()
      .find((history) => versionRange.to <= history.versionTo) ??
    accessibleHistories.at(0);

  if (!maxHistory) {
    return null;
  }

  return {
    from:
      minHistory?.versionFrom != null
        ? minHistory.versionFrom
        : maxHistory.versionFrom,
    to: maxHistory.versionTo,
  };
};

interface PostHistoryViewWithHistoryProps {
  postId: string;
  versionRange: VersionRange;
}

const PostHistoryViewWithHistory = ({
  postId,
  versionRange,
}: PostHistoryViewWithHistoryProps) => {
  const [isEditingVersionName, setIsEditingVersionName] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { data, loading, error } = useQuery(POST_HISTORY_VIEW_QUERY, {
    variables: {
      id: postId,
      versionFrom: versionRange?.from,
      versionTo: versionRange?.to,
    },
  });

  const { post, entityAssetToken } = data ?? {};

  const selectedVersionRange = getSelectedVersionRange(
    post?.histories ?? [],
    versionRange,
  );

  useLayoutEffect(() => {
    if (
      selectedVersionRange?.from != null &&
      selectedVersionRange?.to != null
    ) {
      if (
        versionRange.from === selectedVersionRange.from &&
        versionRange.to === selectedVersionRange.to
      ) {
        return;
      }

      navigate(
        postPath(
          { id: postId, title: '' },
          {
            histories: `${selectedVersionRange.from}-${selectedVersionRange.to}`,
            anchor: location.hash,
          },
        ),
        {
          replace: true,
        },
      );
    }
  }, [
    selectedVersionRange?.from,
    selectedVersionRange?.to,
    versionRange.from,
    versionRange.to,
    postId,
    navigate,
    location.hash,
  ]);

  const { content, editVersionChanges } = useCalculatedVersionChanges({
    diff: post?.historyRange.diff,
    snapshot: post?.historyRange.snapshot,
  });

  const threads = useMemo(
    () => post?.historyRange.threads.filter(isCommentOrPublicCommentThread),
    [post?.historyRange.threads],
  );

  const magneticWallVersionChanges = useMagneticWallVersionChanges({
    threads,
    annotations: post?.historyRange.annotations,
    // it can take up to 2 minutes until a change is saved, because the save interval is 60 seconds
    compareDate: moment(post?.historyRange.insertedAt).subtract(2, 'minutes'),
    content,
  });

  // The error could happen in the following scenarios
  // 1. The post is not found
  // 2. The post is found but the version range is not found
  //
  // In both cases, the error returns with the message "NOT_FOUND",
  // so we can't differentiate at this point. Rather we can try to see
  // if just the version range is wrong, by redirecting to the last history.
  // This case then is separately handled by the PostHistoryViewWithoutVersionRange component.
  //
  // If selectedVersionRange is null, then it means that the history exists, but wasn't accessible
  // because the user doesn't have permission to access it. In this case, we also try out the last history
  // first, which will fall back to `?histories=` if there is no history that the user can access.
  if (error || (!loading && !selectedVersionRange)) {
    return (
      <Navigate
        to={postPath({ id: postId, title: '' }, { histories: 'last' })}
        replace
      />
    );
  }

  if (!post || !selectedVersionRange || !entityAssetToken) {
    return null;
  }

  const { accessibleHistories } = partitionHistories(post.histories);

  // The history field on post returns a calculated id `${postId}-${versionFrom}-${versionTo}`,
  // which does not work with the history name update mutation. Therefore we need to find the history
  // by the version range from the slim histories. For updating the name, it only makes sense to use the
  // exact history matching the version range. If the range spans multiple histories, we can't update the name,
  // and in this case, we don't allow to do so.
  const currentHistory = accessibleHistories.find(
    (history) =>
      history.versionFrom === selectedVersionRange.from &&
      history.versionTo === selectedVersionRange.to,
  );

  // It's okay for the delete version mutation to use the id of the last history in the current range.
  // When multiple histories are shown, we only need to call the mutation with the id of the last history,
  // as all histories before that will be deleted anyways.
  const lastCurrentHistory = selectedVersionRange.to
    ? accessibleHistories.find(
        (history) => history.versionTo === selectedVersionRange.to,
      )
    : null;

  return (
    <AssetTokenProvider entityAssetToken={entityAssetToken}>
      <BasePostHistoryView
        editVersionChanges={editVersionChanges}
        versionRange={selectedVersionRange}
        editedAt={post.historyRange.insertedAt}
        content={content}
        magneticWallVersionChanges={magneticWallVersionChanges}
        post={post}
        topbar={
          <VersionChanges.Topbar
            overflowMenu={
              isEditingVersionName ? null : (
                <PostHistoryViewOverflowMenuItems
                  versionRange={selectedVersionRange}
                  post={post}
                  lastHistoryId={lastCurrentHistory?.id ?? null}
                  onEditVersionName={
                    currentHistory ? () => setIsEditingVersionName(true) : null
                  }
                />
              )
            }
          >
            {isEditingVersionName && currentHistory ? (
              <VersionNameForm
                currentVersionName={currentHistory.name ?? null}
                historyInsertedAt={post.historyRange.insertedAt}
                onReset={() => setIsEditingVersionName(false)}
                historyId={currentHistory.id}
              />
            ) : (
              <VersionChangesDropdown
                post={post}
                version={selectedVersionRange.to}
              />
            )}
          </VersionChanges.Topbar>
        }
      />
    </AssetTokenProvider>
  );
};

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const POST_HISTORY_VIEW_WITHOUT_VERSION_RANGE_QUERY =
  gql(/** @complexity 54 */ `
  query PostHistoryViewWithoutVersionRange($id: ID!) {
    post(id: $id) {
      id
      editedAt
      content{
        id
        delta
      }
      annotations {
        id
        ...BasePostHistoryView_annotation
      }
      histories {
        id
        versionFrom
        versionTo
        ...partitionHistories_history
      }
      threads {
        id
        ...BasePostHistoryView_thread
      }
      ...BasePostHistoryView_post
      ...VersionChangesDropdown_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface PostHistoryViewWithoutVersionRangeProps {
  postId: string;
}

const PostHistoryViewWithoutVersionRange = ({
  postId,
}: PostHistoryViewWithoutVersionRangeProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, error } = useQuery(
    POST_HISTORY_VIEW_WITHOUT_VERSION_RANGE_QUERY,
    {
      fetchPolicy: 'no-cache',
      variables: {
        id: postId,
      },
    },
  );

  const post = data?.post;

  const threads = useMemo(
    () => post?.threads.filter(isCommentOrPublicCommentThread),
    [post?.threads],
  );

  const content = post ? new Delta(post.content.delta) : undefined;

  const magneticWallVersionChanges = useMagneticWallVersionChanges({
    threads,
    annotations: post?.annotations,
    content,
  });

  useLayoutEffect(() => {
    if (!post) return;
    const historiesParam = searchParams.get('histories');

    if (!historiesParam) return;

    if (historiesParam !== 'last') {
      setSearchParams({ histories: '' }, { replace: true });

      return;
    }

    const { accessibleHistories } = partitionHistories(post.histories);
    const lastAccessibleHistory = accessibleHistories.at(0);

    if (!lastAccessibleHistory) {
      setSearchParams({ histories: '' }, { replace: true });
      return;
    }

    const { versionFrom, versionTo } = lastAccessibleHistory;

    // This requires fetchPolicy: 'no-cache' to work properly,
    // so that it doesn't use the cached version of the post.s
    setSearchParams(
      { histories: `${versionFrom}-${versionTo}` },
      { replace: true },
    );
  }, [post, searchParams, setSearchParams]);

  // An error at this point might mean that the post is not found
  // in which case we redirect to the post page, so that PostView handles it
  if (error) {
    return <Navigate to={postPath({ id: postId, title: '' })} replace />;
  }

  if (!post) return null;

  return (
    <AssetTokenProvider entityAssetToken={data?.entityAssetToken}>
      <BasePostHistoryView
        editedAt={post.editedAt}
        content={content}
        magneticWallVersionChanges={magneticWallVersionChanges}
        post={post}
        topbar={
          <VersionChanges.Topbar>
            <VersionChangesDropdown post={post} />
          </VersionChanges.Topbar>
        }
      />
    </AssetTokenProvider>
  );
};

interface PostDoneButtonProps {
  postId: string;
}

export const PostDoneButton = ({ postId }: PostDoneButtonProps) => (
  <Button variant="secondary" to={postPath({ id: postId, title: '' })}>
    Done
  </Button>
);

interface PostHistoryViewProps {
  postId: string;
}

const PostHistoryView = ({ postId }: PostHistoryViewProps) => {
  const [searchParams] = useSearchParams();

  const historiesParam = searchParams.get('histories');
  const [, from, to] = historiesParam?.match(/(\d+)-(\d+)/) ?? [];

  return (
    <Page
      topbar={
        <InternalTopbar postActions={<PostDoneButton postId={postId} />} />
      }
    >
      <QuillContext>
        {from && to ? (
          <PostHistoryViewWithHistory
            postId={postId}
            versionRange={{
              from: parseInt(from, 10),
              to: parseInt(to, 10),
            }}
          />
        ) : (
          <PostHistoryViewWithoutVersionRange postId={postId} />
        )}
      </QuillContext>
    </Page>
  );
};

export default PostHistoryView;
